import React, { useMemo, useState } from 'react';
import type { SeedKey } from '../../hooks/useSeedEffect';
import { useTranslation } from 'react-i18next';
import SchemaForm from '../../schemaForm/SchemaForm';
import type { SchemaFormSection } from '../../schemaForm/types';
import SeasonFieldset, { SeasonFieldsValue } from './SeasonFieldset';
import '../../App.css';

export interface ICourseFormData {
  name: string;
  description: string;
  capacity: string; // empty string = unlimited
  enrollmentPolicy: 'open' | 'approval';
  isActive: boolean;
  pricePerSession: string; // empty string = not offered
  instructorId: number | null;
  termStart: string; // YYYY-MM-DD, empty string = unset
  termEnd: string; // YYYY-MM-DD, empty string = unset
}

interface CourseFormProps {
  initialData?: Partial<ICourseFormData>;
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  onSubmit: (data: ICourseFormData) => void | Promise<void>;
  loading?: boolean;
  submitLabel?: string;
  /**
   * 'create' bootstraps the course's FIRST season, so it renders the six
   * season-owned fields (term / capacity / price / policy / instructor) via the
   * shared SeasonFieldset. 'edit' is COURSE-LEVEL only — those fields moved to
   * the Seasons tab (they are no longer part of the `course` entity meta), so
   * edit renders name / description / isActive only. Defaults to 'edit'.
   */
  mode?: 'create' | 'edit';
}

/**
 * Course settings form.
 *
 * Course-level fields (name / description / isActive) are schema-driven via
 * SchemaForm against the `course` entity meta. Season-owned fields are NOT in
 * that meta any more (Course→Season refactor, migration 0037) — in CREATE mode
 * they are collected through the non-entity SeasonFieldset and merged into the
 * submit payload so a new course still bootstraps its first season. In EDIT mode
 * they are omitted entirely and managed on the Seasons tab.
 *
 * The activity context (`activityId`) is owned by the consuming pages (they pass
 * it separately via ActivityPicker). Presentation fields belong to
 * CoursePresentationForm and are excluded here.
 */
const CourseForm: React.FC<CourseFormProps> = ({
  initialData,
  seedKey,
  onSubmit,
  loading = false,
  submitLabel,
  mode = 'edit',
}) => {
  const { t } = useTranslation();
  const isCreate = mode === 'create';

  // Season-owned fields live outside SchemaForm (not entity-backed). Only used
  // in CREATE mode; seeded from initialData for completeness.
  const [seasonFields, setSeasonFields] = useState<SeasonFieldsValue>(() => ({
    termStart: initialData?.termStart ?? '',
    termEnd: initialData?.termEnd ?? '',
    capacity: initialData?.capacity ?? '',
    pricePerSession: initialData?.pricePerSession ?? '',
    enrollmentPolicy: initialData?.enrollmentPolicy ?? 'approval',
    instructorId: initialData?.instructorId ?? null,
  }));

  const initialValues = useMemo(
    () => ({
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      isActive: initialData?.isActive ?? true,
    }),
    [initialData],
  );

  const termEndError =
    seasonFields.termStart !== '' &&
    seasonFields.termEnd !== '' &&
    seasonFields.termEnd < seasonFields.termStart
      ? t('courses.renew.termOrderError')
      : undefined;

  const sections: SchemaFormSection[] = useMemo(() => {
    const base: SchemaFormSection[] = [{ items: ['name', 'description', 'isActive'] }];
    if (isCreate) {
      base.push({
        items: [
          <SeasonFieldset
            key="season-fieldset"
            value={seasonFields}
            onChange={setSeasonFields}
            termEndError={termEndError}
          />,
        ],
      });
    }
    return base;
  }, [isCreate, seasonFields, termEndError]);

  const handleSubmit = (values: Record<string, any>) => {
    return onSubmit({
      name: values.name,
      description: values.description ?? '',
      isActive: Boolean(values.isActive),
      // Season-owned fields: real values in CREATE, seeded defaults in EDIT
      // (the edit page ignores them — season edits go through the Seasons tab).
      capacity: isCreate ? seasonFields.capacity : (initialData?.capacity ?? ''),
      pricePerSession: isCreate ? seasonFields.pricePerSession : (initialData?.pricePerSession ?? ''),
      enrollmentPolicy: isCreate ? seasonFields.enrollmentPolicy : (initialData?.enrollmentPolicy ?? 'approval'),
      instructorId: isCreate ? seasonFields.instructorId : (initialData?.instructorId ?? null),
      termStart: isCreate ? seasonFields.termStart : (initialData?.termStart ?? ''),
      termEnd: isCreate ? seasonFields.termEnd : (initialData?.termEnd ?? ''),
    });
  };

  return (
    <SchemaForm
      entity="course"
      sections={sections}
      initialValues={initialValues}
      seedKey={seedKey}
      onSubmit={handleSubmit}
      submitLabel={submitLabel}
      submitLabelKey="common.save"
      loading={loading}
    />
  );
};

export default CourseForm;
